<template>
  <Portal>
    <div
      v-if="isOpen"
      :class="$style.dialogOverlay"
      @click="handleOverlayClick"
    >
      <div :class="$style.dialogContent" @click.stop>
        <div v-if="title || showCloseButton" :class="$style.dialogHeader">
          <h2 v-if="title" :class="$style.dialogTitle">{{ title }}</h2>
          <CloseButton v-if="showCloseButton" @click="handleClose" />
        </div>
        <div :class="$style.dialogBody">
          <slot></slot>
        </div>
      </div>
    </div>
  </Portal>
</template>

<script setup lang="ts">
import Portal from "./Portal.vue";
import CloseButton from "./CloseButton.vue";

const props = withDefaults(
  defineProps<{
    isOpen: boolean;
    title?: string;
    showCloseButton?: boolean;
    closeOnOverlayClick?: boolean;
    width?: string;
    maxWidth?: string;
  }>(),
  {
    showCloseButton: true,
    closeOnOverlayClick: true,
    width: "400px",
    maxWidth: "90vw",
  }
);

const emit = defineEmits<{
  (e: "close"): void;
}>();

function handleClose() {
  emit("close");
}

function handleOverlayClick() {
  if (props.closeOnOverlayClick) {
    emit("close");
  }
}
</script>

<style module>
.dialogOverlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  pointer-events: auto;
  backdrop-filter: blur(4px);
  padding: 10px; /* Add padding to prevent edge-to-edge on mobile */
}

.dialogContent {
  background: #2c3e50;
  border-radius: 8px;
  width: v-bind(width);
  max-width: v-bind(maxWidth);
  max-height: 85vh;
  color: white;
  pointer-events: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
}

/* Wider dialogs on larger screens */
@media (min-width: 768px) {
  .dialogContent {
    width: 600px;
  }
}

/* Even wider dialogs on very large screens */
@media (min-width: 1200px) {
  .dialogContent {
    width: 700px;
  }
}

.dialogHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 0 20px;
  flex-shrink: 0;
}

.dialogTitle {
  margin: 0;
  font-size: 1.2em;
  font-weight: 600;
  color: white;
}

.dialogBody {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  min-height: 0;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .dialogContent {
    width: 95%;
    margin: 10px;
    max-height: 90vh; /* More space for content on mobile */
  }

  .dialogHeader {
    padding: 15px 15px 0 15px;
  }

  .dialogBody {
    padding: 15px;
  }
}

/* Medium screens - use default width */
@media (min-width: 481px) and (max-width: 767px) {
  .dialogContent {
    width: v-bind(width); /* Use the prop width */
  }
}

/* Ensure proper scrolling for content-heavy dialogs */
.dialogBody {
  flex: 1;
  min-height: 0; /* Allow flex item to shrink */
  overflow-y: auto; /* Enable scrolling within body */
}
</style>
